import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useUser } from '../context/UserContext';
import API_URL from '../config';
import Mixpanel from '../utils/mixpanel';
import logger from '../utils/logger';


const WeeklySuccessJournal = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [pastDates, setPastDates] = useState([]);
  const [showingPastEntries, setShowingPastEntries] = useState(false);
  const [formData, setFormData] = useState({
    weekStarting: new Date().toISOString().split('T')[0],
    weeklyGoals: '',
    weeklyWins: '',
    weeklyLessons: '',
    nextWeekFocus: '',
    mentalPerformanceRating: 5
  });

  useEffect(() => {
    // Track component view
    Mixpanel.trackFeatureAction('Journal', 'View Weekly Journal');
    
    // Check if there's a journal entry for this week
    checkWeeklyEntry();
  }, []);

  // Check if there's a journal entry for this week
  const checkWeeklyEntry = async (specificDate = null) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const date = specificDate || new Date().toISOString().split('T')[0];
      // Make sure we use the correct API endpoint with /api prefix
      const response = await axios.get(`${API_URL}/api/weekly-journal`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: date }
      });
      
      if (response.data && response.data.entry) {
        // Map backend data structure to frontend structure
        const entry = response.data.entry;
        setFormData({
          weekStarting: entry.date ? new Date(entry.date).toISOString().split('T')[0] : date,
          weeklyGoals: entry.goals || '',
          weeklyWins: entry.wins || '',
          weeklyLessons: entry.lessons || '',
          nextWeekFocus: entry.nextFocus || '',
          mentalPerformanceRating: entry.rating || 5
        });
      }
    } catch (error) {
      logger.error('Error fetching weekly journal entry:', error);
      // No need to show error to user if there's no entry yet
    }
  };

  // Fetch all past journal entries
  const fetchPastJournalEntries = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Get all journal dates for the weekly type
      // Use the correct endpoint for weekly journals with /api prefix
      const response = await axios.get(`${API_URL}/api/weekly-journal/dates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && Array.isArray(response.data)) {
        // Sort dates in descending order (newest first)
        const sortedDates = response.data.sort((a, b) => {
          return new Date(b.date) - new Date(a.date);
        });
        
        setPastDates(sortedDates);
        setShowingPastEntries(true);
      }
    } catch (error) {
      logger.error('Error fetching past journal entries:', error);
      setError('Failed to load past journal entries. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If date is changing, reset the form and check for entries on the new date
    if (name === 'weekStarting' && value !== formData.weekStarting) {
      setFormData({
        weekStarting: value,
        weeklyGoals: '',
        weeklyWins: '',
        weeklyLessons: '',
        nextWeekFocus: '',
        mentalPerformanceRating: 5
      });
      
      // Check if there's an entry for the new date
      setTimeout(() => {
        checkWeeklyEntry(value);
      }, 100);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    setSaved(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSaved(false);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to save a journal entry');
        return;
      }
      
      // Map frontend data structure to backend structure
      const backendData = {
        date: formData.weekStarting,
        goals: formData.weeklyGoals,
        wins: formData.weeklyWins,
        lessons: formData.weeklyLessons,
        nextFocus: formData.nextWeekFocus,
        rating: formData.mentalPerformanceRating,
        type: 'weekly'
      };
      
      // Make sure we use the correct API endpoint with /api prefix
      await axios.post(`${API_URL}/api/journal`, backendData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSaved(true);
      
      // Track successful journal entry
      Mixpanel.trackFeatureAction('Journal', 'Weekly Journal Saved', {
        weekStarting: formData.weekStarting,
        mentalPerformanceRating: formData.mentalPerformanceRating
      });
      
    } catch (error) {
      logger.error('Error saving weekly journal entry:', error);
      setError('Failed to save your weekly journal entry. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleNewEntry = () => {
    setFormData({
      weekStarting: new Date().toISOString().split('T')[0],
      weeklyGoals: '',
      weeklyWins: '',
      weeklyLessons: '',
      nextWeekFocus: '',
      mentalPerformanceRating: 5
    });
    setSaved(false);
    setShowingPastEntries(false);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-6">Weekly Success Journal</h2>
      
      {error && (
        <div className="alert alert-error mb-4">
          <span>{error}</span>
        </div>
      )}
      
      {saved ? (
        <div className="text-center py-8">
          <div className="alert alert-success mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span>Your weekly journal entry has been saved successfully!</span>
          </div>
          <button 
            onClick={handleNewEntry}
            className="btn btn-primary"
          >
            Create New Entry
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="flex justify-between items-center mb-4">
            <button
              type="button"
              onClick={fetchPastJournalEntries}
              className="btn btn-secondary btn-sm"
            >
              {showingPastEntries ? "Hide Past Entries" : "Review Past Journals"}
            </button>
            
            {showingPastEntries && (
              <button
                type="button"
                onClick={handleNewEntry}
                className="btn btn-outline btn-sm"
              >
                New Entry
              </button>
            )}
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="weekStarting">
              Week Starting
            </label>
            {showingPastEntries ? (
              <select
                id="weekStarting"
                name="weekStarting"
                value={formData.weekStarting}
                onChange={handleInputChange}
                className="select select-bordered w-full"
                required
              >
                <option value="" disabled>Select a week</option>
                {pastDates.map(entry => (
                  <option key={entry._id || entry.date} value={entry.date}>
                    Week of {new Date(entry.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="date"
                id="weekStarting"
                name="weekStarting"
                value={formData.weekStarting}
                onChange={handleInputChange}
                className="input input-bordered w-full"
                required
              />
            )}
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="mentalPerformanceRating">
              Mental Performance Rating (0-10)
            </label>
            <input
              type="range"
              id="mentalPerformanceRating"
              name="mentalPerformanceRating"
              min="0"
              max="10"
              value={formData.mentalPerformanceRating}
              onChange={handleInputChange}
              className="range range-primary w-full"
            />
            <div className="flex justify-between text-xs mt-1">
              <span>0</span>
              <span>1</span>
              <span>2</span>
              <span>3</span>
              <span>4</span>
              <span>5</span>
              <span>6</span>
              <span>7</span>
              <span>8</span>
              <span>9</span>
              <span>10</span>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="weeklyGoals">
              Weekly Goals
            </label>
            <textarea
              id="weeklyGoals"
              name="weeklyGoals"
              value={formData.weeklyGoals}
              onChange={handleInputChange}
              className="textarea textarea-bordered w-full h-24"
              placeholder="What were your goals for this week?"
              required
            ></textarea>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="weeklyWins">
              Weekly Wins
            </label>
            <textarea
              id="weeklyWins"
              name="weeklyWins"
              value={formData.weeklyWins}
              onChange={handleInputChange}
              className="textarea textarea-bordered w-full h-24"
              placeholder="What went well this week? What did you accomplish?"
              required
            ></textarea>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="weeklyLessons">
              Lessons Learned
            </label>
            <textarea
              id="weeklyLessons"
              name="weeklyLessons"
              value={formData.weeklyLessons}
              onChange={handleInputChange}
              className="textarea textarea-bordered w-full h-24"
              placeholder="What did you learn this week? What could you improve?"
              required
            ></textarea>
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="nextWeekFocus">
              Next Week's Focus
            </label>
            <textarea
              id="nextWeekFocus"
              name="nextWeekFocus"
              value={formData.nextWeekFocus}
              onChange={handleInputChange}
              className="textarea textarea-bordered w-full h-24"
              placeholder="What will you focus on next week?"
              required
            ></textarea>
          </div>
          
          <div className="flex justify-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading loading-spinner loading-xs"></span>
                  Saving...
                </>
              ) : 'Save Weekly Journal'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default WeeklySuccessJournal;